import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import { JCONSULT_LOGO, JCONSULT_LOGO_MARK } from '../../config/brandingAssets';
import { PUBLIC_HOME_NAV_ITEMS } from '../../config/publicHomeNav';

/** Emblème menu public (repli sur logo complet si besoin). */
const publicNavLogoSrc = JCONSULT_LOGO_MARK || JCONSULT_LOGO;

type Props = {
  isAuthenticated?: boolean;
  isAdmin?: boolean;
  spaceLabel?: string;
  onGoToSpace?: () => void;
};

function navLinkClassName(isActive: boolean) {
  const base =
    'block rounded-lg px-3 py-3 text-sm font-semibold transition md:inline-block md:rounded-none md:border-b-2 md:border-transparent md:px-0 md:py-1 md:hover:bg-transparent';
  if (isActive) {
    return `${base} text-primary underline decoration-primary decoration-2 underline-offset-[6px] md:text-primary md:underline md:decoration-primary md:decoration-2 md:underline-offset-[8px]`;
  }
  return `${base} text-on-surface-variant hover:bg-primary/5 hover:text-primary md:hover:border-primary/30`;
}

function PublicNavLink({
  hash,
  label,
  sectionId,
  activeSection,
  onNavigate,
}: {
  hash: string;
  label: string;
  sectionId: string;
  activeSection: string;
  onNavigate: () => void;
}) {
  const { pathname } = useLocation();
  const isHome = pathname === '/';
  const isFilieresRoute = pathname === '/filieres' || pathname.startsWith('/filieres/');
  const isActive =
    (isHome && activeSection === sectionId) || (sectionId === 'filieres' && isFilieresRoute);
  const className = navLinkClassName(isActive);

  if (isHome) {
    return (
      <a
        href={hash}
        className={className}
        aria-current={isActive ? 'location' : undefined}
        onClick={onNavigate}
      >
        {label}
      </a>
    );
  }

  return (
    <Link
      to={`/${hash}`}
      className={className}
      aria-current={isActive ? 'location' : undefined}
      onClick={onNavigate}
    >
      {label}
    </Link>
  );
}

function resolveHashSection(hash: string) {
  const id = hash.replace('#', '');
  return PUBLIC_HOME_NAV_ITEMS.some((item) => item.id === id) ? id : '';
}

/** Hauteur du header sticky (h-20) + marge pour le repère de section active. */
const HEADER_SCROLL_OFFSET = 96;

function pickActiveSectionFromScroll(): string {
  const scrollPosition = window.scrollY + HEADER_SCROLL_OFFSET;

  for (let i = PUBLIC_HOME_NAV_ITEMS.length - 1; i >= 0; i -= 1) {
    const { id } = PUBLIC_HOME_NAV_ITEMS[i];
    const el = document.getElementById(id);
    if (!el) continue;
    const sectionTop = el.getBoundingClientRect().top + window.scrollY;
    if (sectionTop <= scrollPosition) {
      return id;
    }
  }

  return '';
}

export default function PublicSiteHeader({
  isAuthenticated,
  isAdmin,
  spaceLabel,
  onGoToSpace,
}: Props) {
  const portalButtonLabel = spaceLabel ?? (isAdmin ? 'Administration' : 'Mon espace');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!isHome) {
      setActiveSection('');
      return undefined;
    }

    const syncFromScroll = () => {
      const next = pickActiveSectionFromScroll();
      setActiveSection((prev) => (prev === next ? prev : next));
    };

    let rafId = 0;
    const onScrollOrResize = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(syncFromScroll);
    };

    const fromHash = resolveHashSection(location.hash);
    syncFromScroll();
    const retryTimer = window.setTimeout(syncFromScroll, 150);
    const hashRetryTimer = fromHash ? window.setTimeout(syncFromScroll, 450) : undefined;

    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
      window.clearTimeout(retryTimer);
      if (hashRetryTimer != null) window.clearTimeout(hashRetryTimer);
      cancelAnimationFrame(rafId);
    };
  }, [isHome, location.pathname, location.hash]);

  useEffect(() => {
    if (!mobileMenuOpen) {
      document.body.style.overflow = '';
      return undefined;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <header className="sticky top-0 z-50 border-b border-outline-variant/15 bg-white/95 backdrop-blur-md">
      <div className="mx-auto flex h-20 max-w-6xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex shrink-0 items-center overflow-visible" onClick={closeMobileMenu}>
          <img
            src={publicNavLogoSrc}
            alt="JCONSULT MY Academia"
            className="h-auto w-32 max-h-14 object-contain py-1 sm:w-36 sm:max-h-[3.75rem]"
          />
        </Link>

        <nav className="hidden items-center gap-6 font-semibold text-on-surface-variant md:flex lg:gap-8">
          {PUBLIC_HOME_NAV_ITEMS.map((item) => (
            <PublicNavLink
              key={item.id}
              hash={item.hash}
              label={item.label}
              sectionId={item.id}
              activeSection={activeSection}
              onNavigate={closeMobileMenu}
            />
          ))}
        </nav>

        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            className="flex h-10 w-10 items-center justify-center rounded-public-btn border border-outline-variant/20 text-on-surface-variant transition hover:bg-primary/5 md:hidden"
            aria-expanded={mobileMenuOpen}
            aria-controls="public-mobile-nav"
            aria-label={mobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            onClick={() => setMobileMenuOpen((open) => !open)}
          >
            <LucideSymbolIcon name={mobileMenuOpen ? 'close' : 'menu'} tone="primary" size={22} />
          </button>

          {isAuthenticated ? (
            <button
              type="button"
              onClick={() => {
                closeMobileMenu();
                onGoToSpace?.();
              }}
              className="rounded-public-btn bg-primary px-3 py-2 text-sm font-bold text-white shadow-sm hover:opacity-90 sm:px-4"
            >
              {portalButtonLabel}
            </button>
          ) : (
            <>
              <Link
                to="/login"
                className="hidden rounded-public-btn border border-primary/30 px-4 py-2 text-sm font-semibold text-primary sm:inline-block"
                onClick={closeMobileMenu}
              >
                Connexion
              </Link>
              <Link
                to="/login?mode=register"
                className="rounded-public-btn bg-primary px-3 py-2 text-sm font-bold text-white shadow-sm hover:opacity-90 sm:px-4"
                onClick={closeMobileMenu}
              >
                S&apos;inscrire
              </Link>
            </>
          )}
        </div>
      </div>

      {mobileMenuOpen ? (
        <>
          <button
            type="button"
            className="fixed inset-0 top-20 z-40 bg-black/40 md:hidden"
            aria-label="Fermer le menu"
            onClick={closeMobileMenu}
          />
          <nav
            id="public-mobile-nav"
            className="relative z-50 border-t border-outline-variant/15 bg-white px-4 py-4 shadow-lg md:hidden"
          >
            <ul className="space-y-1">
              {PUBLIC_HOME_NAV_ITEMS.map((item) => (
                <li key={item.id}>
                  <PublicNavLink
                    hash={item.hash}
                    label={item.label}
                    sectionId={item.id}
                    activeSection={activeSection}
                    onNavigate={closeMobileMenu}
                  />
                </li>
              ))}
            </ul>
          </nav>
        </>
      ) : null}
    </header>
  );
}
